/**
 * ImageFeedback component - Collect refinement feedback from users.
 *
 * Design: DENiZEN
 * - Soft shadows and rounded corners
 * - Muted palette with mid-green and teal accents
 * - High contrast buttons
 * - WCAG 2.1 AA compliant
 */

import { useState } from 'react'

export interface ImageFeedbackProps {
  /**
   * Current iteration number (1-3)
   */
  iterationNumber: number
  /**
   * Maximum allowed iterations
   */
  maxIterations?: number
  /**
   * Callback when user accepts the image
   */
  onAccept: () => void
  /**
   * Callback when user requests refinement
   */
  onRefine: (feedback: string) => void
  /**
   * Whether a request is in progress
   */
  isLoading?: boolean
  /**
   * Error message to display
   */
  error?: string | null
}

/**
 * Suggested feedback prompts
 */
const SUGGESTED_PROMPTS = [
  'Add more green spaces',
  'More people interacting',
  'Warmer colours',
  'Community gathering areas',
  'Accessibility features',
]

export function ImageFeedback({
  iterationNumber,
  maxIterations = 3,
  onAccept,
  onRefine,
  isLoading = false,
  error,
}: ImageFeedbackProps) {
  const [feedback, setFeedback] = useState('')
  const [showFeedbackForm, setShowFeedbackForm] = useState(false)

  const canRefine = iterationNumber < maxIterations
  const iterationsRemaining = maxIterations - iterationNumber

  const handleRefine = () => {
    if (feedback.trim() && !isLoading) {
      onRefine(feedback.trim())
      setFeedback('')
      setShowFeedbackForm(false)
    }
  }

  const handleSuggestedPrompt = (prompt: string) => {
    setFeedback(prompt)
  }

  return (
    <div className="denizen-card p-6 relative animate-slide-up">
      {/* Round label */}
      <div className="pebble-label absolute -top-3 -right-3 text-xs">
        FEEDBACK
      </div>

      {/* Title */}
      <h3 className="text-xl font-display font-bold text-dark-green tracking-tight mb-2">
        What do you <span className="highlight-marker">think</span>?
      </h3>

      {/* Iteration info */}
      <p className="text-sm font-body text-muted mb-4">
        {canRefine ? (
          <>
            You can refine this image{' '}
            <span className="font-display font-bold text-dark-green">
              {iterationsRemaining} more {iterationsRemaining === 1 ? 'time' : 'times'}
            </span>.
          </>
        ) : (
          <>This is your final iteration. You can accept or download the image.</>
        )}
      </p>

      {/* Error display - DENiZEN style */}
      {error && (
        <div
          className="mb-4 p-4 bg-error border border-dark-green/15 shadow-denizen-sm rounded-2xl"
          role="alert"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white border border-dark-green/15 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-error" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <p className="text-white text-sm font-body">{error}</p>
          </div>
        </div>
      )}

      {/* Action buttons */}
      {!showFeedbackForm ? (
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Accept button - mid-green */}
          <button
            type="button"
            onClick={onAccept}
            disabled={isLoading}
            className={`
              flex-1 py-4 px-6 font-display font-bold text-sm tracking-wider
              border border-dark-green/15 rounded-2xl
              shadow-denizen-sm
              transition-all duration-150
              focus:outline-none focus-visible:ring-4 focus-visible:ring-mid-green focus-visible:ring-offset-2
              ${isLoading
                ? 'bg-muted text-white cursor-not-allowed'
                : 'bg-dark-green text-white hover:-translate-y-1 active:shadow-none'
              }
            `}
          >
            <span className="flex items-center justify-center gap-2">
              <svg
                className="w-5 h-5"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M19.916 4.626a.75.75 0 0 1 .208 1.04l-9 13.5a.75.75 0 0 1-1.154.114l-6-6a.75.75 0 0 1 1.06-1.06l5.353 5.353 8.493-12.74a.75.75 0 0 1 1.04-.207Z"
                  clipRule="evenodd"
                />
              </svg>
              Accept Image
            </span>
          </button>

          {/* Refine button - White */}
          {canRefine && (
            <button
              type="button"
              onClick={() => setShowFeedbackForm(true)}
              disabled={isLoading}
              className={`
                flex-1 py-4 px-6 font-display font-bold text-sm tracking-wider
                border border-dark-green/15 bg-white text-dark-green rounded-2xl
                shadow-denizen-sm
                transition-all duration-150
                focus:outline-none focus-visible:ring-4 focus-visible:ring-mid-green focus-visible:ring-offset-2
                ${isLoading
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:-translate-y-1 active:shadow-none'
                }
              `}
            >
              <span className="flex items-center justify-center gap-2">
                <svg
                  className="w-5 h-5"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M21.731 2.269a2.625 2.625 0 0 0-3.712 0l-1.157 1.157 3.712 3.712 1.157-1.157a2.625 2.625 0 0 0 0-3.712ZM19.513 8.199l-3.712-3.712-8.4 8.4a5.25 5.25 0 0 0-1.32 2.214l-.8 2.685a.75.75 0 0 0 .933.933l2.685-.8a5.25 5.25 0 0 0 2.214-1.32l8.4-8.4Z" />
                  <path d="M5.25 5.25a3 3 0 0 0-3 3v10.5a3 3 0 0 0 3 3h10.5a3 3 0 0 0 3-3V13.5a.75.75 0 0 0-1.5 0v5.25a1.5 1.5 0 0 1-1.5 1.5H5.25a1.5 1.5 0 0 1-1.5-1.5V8.25a1.5 1.5 0 0 1 1.5-1.5h5.25a.75.75 0 0 0 0-1.5H5.25Z" />
                </svg>
                Refine Image
              </span>
            </button>
          )}
        </div>
      ) : (
        /* Feedback form - DENiZEN style */
        <div className="space-y-4">
          {/* Feedback textarea */}
          <div>
            <label
              htmlFor="refinementFeedback"
              className="block text-xs font-display font-bold text-dark-green tracking-wider mb-2"
            >
              What would you like to change?
            </label>
            <textarea
              id="refinementFeedback"
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Describe the changes you'd like to see..."
              rows={3}
              className="w-full px-4 py-3 font-body text-dark-green bg-white border border-dark-green/15 shadow-denizen-sm rounded-2xl placeholder:text-muted focus:outline-none focus:shadow-denizen focus:-translate-y-1 transition-all duration-150 resize-none"
              aria-describedby="feedback-hint"
            />
            <p id="feedback-hint" className="mt-2 text-xs font-body text-muted">
              Be specific about what you'd like to add, remove, or change.
            </p>
          </div>

          {/* Suggested prompts - DENiZEN chips */}
          <div>
            <p className="text-xs font-display font-bold text-dark-green tracking-wider mb-2">
              Suggestions:
            </p>
            <div className="flex flex-wrap gap-2">
              {SUGGESTED_PROMPTS.map((prompt) => (
                <button
                  key={prompt}
                  type="button"
                  onClick={() => handleSuggestedPrompt(prompt)}
                  className="px-3 py-1 text-xs font-body bg-white border border-dark-green/15 text-dark-green hover:bg-light-green hover:-translate-y-0.5 transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-mid-green rounded-2xl"
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>

          {/* Submit/Cancel buttons */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => {
                setShowFeedbackForm(false)
                setFeedback('')
              }}
              disabled={isLoading}
              className="flex-1 py-3 px-4 font-display font-bold text-sm tracking-wider border border-dark-green/15 bg-white text-dark-green shadow-denizen-sm rounded-2xl hover:-translate-y-1 active:shadow-none transition-all duration-150 focus:outline-none focus-visible:ring-4 focus-visible:ring-mid-green"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleRefine}
              disabled={isLoading || !feedback.trim()}
              className={`
                flex-1 py-3 px-4 font-display font-bold text-sm tracking-wider
                border border-dark-green/15 rounded-2xl
                shadow-denizen-sm
                transition-all duration-150
                focus:outline-none focus-visible:ring-4 focus-visible:ring-mid-green focus-visible:ring-offset-2
                ${isLoading || !feedback.trim()
                  ? 'bg-muted text-white cursor-not-allowed'
                  : 'bg-dark-green text-white hover:-translate-y-1 active:shadow-none'
                }
              `}
              aria-busy={isLoading || undefined}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg
                    className="w-4 h-4 animate-spin"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Generating...
                </span>
              ) : (
                'Generate New'
              )}
            </button>
          </div>
        </div>
      )}

      {/* Iteration indicator - soft dots */}
      <div className="mt-6 pt-4 border-t border-dark-green/15">
        <div className="flex items-center justify-between">
          <span className="text-xs font-display font-bold text-dark-green">
            Version {iterationNumber} of {maxIterations}
          </span>
          <div className="flex gap-2">
            {Array.from({ length: maxIterations }).map((_, i) => (
              <div
                key={i}
                className={`
                  w-4 h-4 rounded-full border border-dark-green/15
                  ${i < iterationNumber ? 'bg-mid-green shadow-denizen-sm' : 'bg-white'}
                `}
                aria-hidden="true"
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ImageFeedback
